import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TEMPLATES = [["classic", "Classic"], ["modern", "Modern"], ["compact", "Compact"]];

function useSettingsForm() {
  const { t, refresh } = useI18n();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)).catch(e => toast.error(formatErr(e))); }, []);
  const set = (k, v) => setS(x => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", s); toast.success(t("settings.saved", "Settings saved")); await refresh(); }
    catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };
  return { t, s, set, save, saving };
}

function SettingsShell({ t, titleKey, titleDefault, subtitle, testId, saving, onSave, children }) {
  return (
    <div className="p-8 max-w-5xl" onKeyDownCapture={handleSimpleFormNav} data-testid={`${testId}-page`}>
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">{t("nav.settings", "Settings")}</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter" data-testid="settings-title">{t(titleKey, titleDefault)}</h1>
        <div className="text-xs text-muted-foreground mt-2">{subtitle}</div>
      </div>
      {children}
      <div className="border-t border-border pt-4 mt-6">
        <Button data-testid="save-settings-btn" onClick={onSave} disabled={saving} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">{saving ? t("common.saving") : t("common.save")}</Button>
      </div>
    </div>
  );
}

function CheckboxRow({ s, set, k, label }) {
  return (
    <label className="flex items-center justify-between border border-border rounded-sm p-3 cursor-pointer hover:bg-secondary/40" data-testid={`row-${k}`}>
      <span className="text-sm">{label}</span>
      <Checkbox data-testid={`toggle-${k}`} checked={!!s[k]} onCheckedChange={(v) => set(k, !!v)}/>
    </label>
  );
}

export function SettingsFinancial() {
  const { t, s, set, save, saving } = useSettingsForm();
  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;
  return (
    <SettingsShell t={t} titleKey="settings.tab.financial" titleDefault="Financial" testId="settings-financial"
      subtitle={t("settings.subtitle", "System-wide preferences (Owner only).")} saving={saving} onSave={save}>
      <div className="space-y-3" data-testid="tab-content-financial">
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("settings.fy.start")}</Label><Input data-testid="fy-start" value={s.fy_start} onChange={(e) => set("fy_start", e.target.value)} placeholder="04-01" className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("settings.fy.end")}</Label><Input data-testid="fy-end" value={s.fy_end} onChange={(e) => set("fy_end", e.target.value)} placeholder="03-31" className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("settings.fy.current")}</Label><Input data-testid="fy-label" value={s.fy_label} onChange={(e) => set("fy_label", e.target.value)} placeholder="2026-27" className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
        </div>
      </div>
    </SettingsShell>
  );
}

export function SettingsTransaction() {
  const { t, s, set, save, saving } = useSettingsForm();
  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;
  return (
    <SettingsShell t={t} titleKey="settings.tab.transaction" titleDefault="Transaction" testId="settings-transaction"
      subtitle={t("settings.subtitle", "System-wide preferences (Owner only).")} saving={saving} onSave={save}>
      <div className="space-y-3" data-testid="tab-content-transaction">
        <div className="grid grid-cols-4 gap-3">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("settings.txn.prefix.sale")}</Label><Input data-testid="prefix-sale" value={s.prefix_sale} onChange={(e) => set("prefix_sale", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("settings.txn.prefix.purchase")}</Label><Input data-testid="prefix-purchase" value={s.prefix_purchase} onChange={(e) => set("prefix_purchase", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("settings.txn.prefix.jobcard")}</Label><Input data-testid="prefix-jobcard" value={s.prefix_jobcard} onChange={(e) => set("prefix_jobcard", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("settings.txn.prefix.po")}</Label><Input data-testid="prefix-po" value={s.prefix_po} onChange={(e) => set("prefix_po", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("settings.txn.format")}</Label>
          <Input data-testid="numbering-format" value={s.numbering_format} onChange={(e) => set("numbering_format", e.target.value)} placeholder="{PREFIX}-{YY}{MM}{DD}-{SEQ04}" className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{t("settings.txn.format.help")}</div>
        </div>
        <CheckboxRow s={s} set={set} k="auto_payment_panel" label={t("settings.txn.auto_pay")}/>
        <CheckboxRow s={s} set={set} k="batch_entry_for_sale" label={t("settings.txn.batch_entry")}/>
      </div>
    </SettingsShell>
  );
}

export function SettingsPrint() {
  const { t, s, set, save, saving } = useSettingsForm();
  const [templates, setTemplates] = useState([]);
  const loadTemplates = () => api.get("/print-templates").then(r => setTemplates(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { loadTemplates(); }, []);
  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;
  return (
    <SettingsShell t={t} titleKey="settings.tab.print" titleDefault="Print & PDF" testId="settings-print"
      subtitle={t("settings.subtitle", "System-wide preferences (Owner only).")} saving={saving} onSave={save}>
      <div className="space-y-3" data-testid="tab-content-print">
        {[["default_invoice_tpl", "settings.print.tpl.invoice"], ["default_jobcard_tpl", "settings.print.tpl.jobcard"], ["default_po_tpl", "settings.print.tpl.po"]].map(([k, tk]) => (
          <div key={k}>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t(tk)}</Label>
            <Select value={s[k]} onValueChange={(v) => set(k, v)}>
              <SelectTrigger data-testid={`tpl-${k}`} className="mt-1 rounded-sm bg-secondary border-border w-64"><SelectValue/></SelectTrigger>
              <SelectContent>{TEMPLATES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Default Print Template</Label>
          <Select value={s.default_print_template || "none"} onValueChange={(v) => set("default_print_template", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="tpl-default-print-template" className="mt-1 rounded-sm bg-secondary border-border w-64"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" data-testid="tpl-default-opt-none">— None —</SelectItem>
              {templates.filter(tp => tp.status === "Active").map(tp => (
                <SelectItem key={tp.id} value={tp.id} data-testid={`tpl-default-opt-${tp.id}`}>{tp.name} ({tp.paper_size} · {tp.orientation})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Pick any saved custom template as the default for printing</div>
        </div>
        <CheckboxRow s={s} set={set} k="auto_print_after_save" label={t("settings.print.auto_print")}/>
      </div>
    </SettingsShell>
  );
}

export function SettingsNotification() {
  const { t, s, set, save, saving } = useSettingsForm();
  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;
  return (
    <SettingsShell t={t} titleKey="settings.tab.notification" titleDefault="Notification" testId="settings-notification"
      subtitle={t("settings.subtitle", "System-wide preferences (Owner only).")} saving={saving} onSave={save}>
      <div className="space-y-3" data-testid="tab-content-notification">
        <CheckboxRow s={s} set={set} k="alert_reorder" label={t("settings.notif.reorder")}/>
        <CheckboxRow s={s} set={set} k="alert_warranty_expiry" label={t("settings.notif.warranty")}/>
      </div>
    </SettingsShell>
  );
}

export function SettingsLanguage() {
  const { t, s, set, save, saving } = useSettingsForm();
  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;
  return (
    <SettingsShell t={t} titleKey="settings.tab.language" titleDefault="Language" testId="settings-language"
      subtitle={t("settings.subtitle", "System-wide preferences (Owner only).")} saving={saving} onSave={save}>
      <div className="space-y-3 max-w-md" data-testid="tab-content-language">
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t("settings.lang.app")}</Label>
          <Select value={s.language} onValueChange={(v) => set("language", v)}>
            <SelectTrigger data-testid="lang-select" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="hi">हिन्दी (Hindi)</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{t("settings.lang.help")}</div>
        </div>
      </div>
    </SettingsShell>
  );
}
